/**
 * Tiny markdown renderer for help articles.
 *
 * Deliberately NOT react-markdown: v9+ is ESM-only and breaks CRA's
 * bundled jest (the same class of resolution pain as react-router v7).
 * Help articles are authored in-repo against this exact subset, so a
 * 60-line renderer is safer than a dependency tree:
 *
 *   # ## ###   headings          - item      bullet list
 *   **bold**   `inline code`     1. item     numbered list
 *   > quote    blank line = paragraph break
 */

import React from 'react';

// Inline: **bold** and `code`.
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith('`') && p.endsWith('`')) {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-slate-100 text-[12px] font-mono text-slate-700">
          {p.slice(1, -1)}
        </code>
      );
    }
    return p;
  });
};

export default function renderMarkdown(body) {
  const lines = body.split('\n');
  const out = [];
  let list = null; // {ordered, items}
  let key = 0;

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? 'ol' : 'ul';
    out.push(
      <Tag
        key={key++}
        className={`${list.ordered ? 'list-decimal' : 'list-disc'} list-outside ml-5 mb-3 space-y-1`}
      >
        {list.items.map((item, i) => (
          <li key={i} className="text-[13px] text-slate-600 leading-relaxed">{renderInline(item)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,3})\s+(.*)/);
    const li = line.match(/^-\s+(.*)/);
    const ol = line.match(/^\d+\.\s+(.*)/);
    const quote = line.match(/^>\s?(.*)/);

    if (h) {
      flushList();
      const level = h[1].length;
      const cls = level === 1
        ? 'text-[17px] font-semibold text-slate-900 mb-3'
        : level === 2
        ? 'text-[14px] font-semibold text-slate-800 mt-5 mb-2'
        : 'text-[13px] font-semibold text-slate-700 mt-4 mb-1.5';
      out.push(React.createElement(`h${level + 1}`, { key: key++, className: cls }, renderInline(h[2])));
    } else if (li) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(li[1]);
    } else if (ol) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(ol[1]);
    } else if (quote) {
      flushList();
      out.push(
        <blockquote key={key++} className="border-l-2 border-blue-200 bg-blue-50/40 px-3 py-2 rounded-r mb-3 text-[12px] text-slate-600">
          {renderInline(quote[1])}
        </blockquote>,
      );
    } else if (line.trim() === '') {
      flushList();
    } else {
      flushList();
      out.push(
        <p key={key++} className="text-[13px] text-slate-600 leading-relaxed mb-3">
          {renderInline(line)}
        </p>,
      );
    }
  }
  flushList();
  return out;
}
